import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { useTranslations } from 'next-intl';
import ShapedImage from '@components/molecules/shaped-image/shapedImage';
import Container from '@components/layout/container/container';
import CompactLoginMask from '@components/molecules/compact-login-mask/compactLoginMask';
import Headline from '@components/atoms/headline/headline';
import Copy from '@components/atoms/copy/copy';
import ArrowLink from '@components/molecules/arrow-link/arrowLink';
import StyledExhibitionTabTeaser from './exhibitionTabTeaser.style';
import LoginIcon from '../../icons/login.svg';

const ExhibitionTabTeaser = ({ data, loginMask }) => {
  const t = useTranslations();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [closedInput, setClosedInput] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setClosedInput(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 1.0
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, []);

  const ariaLabel =
    data.teasers && data.teasers.length > 0
      ? data.teasers
          .map(t => t.headline || t.tabTitle)
          .filter(Boolean)
          .join(' + ')
      : 'Tabs';

  const handleTabClick = index => {
    setSelectedTabIndex(index);
  };

  const handleKeyDown = (event, index) => {
    const tabCount = data.teasers.length;
    let newIndex = index;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (index + 1) % tabCount;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = (index - 1 + tabCount) % tabCount;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = tabCount - 1;
        break;
      default:
        return;
    }

    setSelectedTabIndex(newIndex);
    const newTab = document.getElementById(`tab-${data.teasers[newIndex].id}`);
    if (newTab) newTab.focus();
  };

  return (
    <StyledExhibitionTabTeaser aria-label={ariaLabel}>
      <div className="tab-teaser" id={data.id}>
        {loginMask && <div ref={sentinelRef} style={{ height: 1, width: '100%' }} />}
        {loginMask && (
          <div className="tab-teaser__sticky">
            <div className="tab-teaser__loginMask-close">
              <button
                type="button"
                className="tab-teaser__loginMask-open-button"
                tabIndex={closedInput ? 0 : -1}
                onClick={() => setClosedInput(false)}
              >
                <span className="sr-only">{t('openExhibitionCode')}</span>
                <LoginIcon
                  role="img"
                  ariaLabel={t('openExhibitionCode')}
                  className="loginMask__icon"
                />
              </button>
            </div>
            <div
              className={`tab-teaser__loginMask-open ${
                closedInput ? 'animate-fadeOut' : 'animate-fadeIn'
              }`}
            >
              <CompactLoginMask
                {...loginMask}
                topLine={loginMask.headline ?? ''}
                ariaLabel="Tabs"
              />
            </div>
          </div>
        )}
        <Container large>
          <div
            className="tab-teaser__nav"
            role="tablist"
            aria-label={ariaLabel}
            aria-orientation="horizontal"
          >
            {data.teasers.map((item, index) => {
              const isSelected = index === selectedTabIndex;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabClick(index)}
                  onKeyDown={e => handleKeyDown(e, index)}
                  type="button"
                  role="tab"
                  className={`tab-teaser__nav-item ${isSelected ? 'is--active' : ''}`}
                  aria-controls={`panel-${item.id}`}
                  id={`tab-${item.id}`}
                  aria-selected={isSelected}
                  tabIndex={isSelected ? 0 : -1}
                >
                  <Headline primary>{item.tabTitle}</Headline>
                  {isSelected ? (
                    <motion.div className="tab-teaser__nav-item-underline" layoutId="underline" />
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="tab-teaser__panels">
            {data.teasers.map((item, index) => {
              const isSelected = index === selectedTabIndex;
              return (
                <div
                  key={item.id}
                  role="tabpanel"
                  id={`panel-${item.id}`}
                  aria-labelledby={`tab-${item.id}`}
                  className={`tab-teaser__panel ${isSelected ? 'is--active' : ''}`}
                  style={{
                    opacity: isSelected ? 1 : 0,
                    height: isSelected ? 'auto' : 0,
                    overflow: 'hidden',
                    transition: 'opacity 0.3s ease, height 0.3s ease'
                  }}
                  hidden={!isSelected}
                >
                  <div className="tab-teaser__content">
                    {item.featuredImage[0] && (
                      <div className="tab-teaser__content-image">
                        <ShapedImage
                          clipForm={`${
                            index === 0
                              ? 'polygon(0 9%, 80% 0, 100% 11%, 92% 100%, 25% 100%, 0 91%)'
                              : 'polygon(35% 15%, 67% 0, 100% 11%, 89% 94%, 41% 100%, 0 83%, 7% 8%)'
                          }`}
                          alt={item.featuredImage[0].alt || item.tabTitle}
                          src={item.featuredImage[0].url}
                          srcset={item.featuredImage[0].srcset}
                          sizes="100vw"
                        />
                      </div>
                    )}
                    <div className="tab-teaser__content-text">
                      <div className="tab-teaser__content-text-headline">
                        {item.topline && (
                          <Headline tag="span" subline>
                            {item.topline}
                          </Headline>
                        )}
                        {item.headline && (
                          <Headline tag="h3" secondary>
                            {item.headline}
                          </Headline>
                        )}
                      </div>
                      <div className="tab-teaser__content-text-description">
                        {item.copy && <Copy>{item.copy}</Copy>}
                        {item.cta && (
                          <div className="tab-teaser__content-link">
                            <ArrowLink
                              href={item.cta}
                              size="50"
                              srTitle={`${
                                item.headline
                                  ? `${t('learnMoreAbout')} "${item.headline}"`
                                  : t('learnMore')
                              }`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </div>
    </StyledExhibitionTabTeaser>
  );
};

ExhibitionTabTeaser.propTypes = {
  loginMask: PropTypes.shape(),
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    teasers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        tabTitle: PropTypes.string.isRequired,
        featuredImage: PropTypes.arrayOf(PropTypes.shape()),
        topline: PropTypes.string,
        headline: PropTypes.string,
        copy: PropTypes.string,
        cta: PropTypes.string
      })
    ).isRequired
  }).isRequired
};

export default ExhibitionTabTeaser;
